import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft, Calculator } from "lucide-react";
import { STAFFING_LOCS, STAFFING_ROLES } from "@/data/instruments";

export default function Staffing() {
  const navigate = useNavigate();
  const [org, setOrg] = useState("");
  const [notes, setNotes] = useState("");
  const [census, setCensus] = useState(() => Object.fromEntries(STAFFING_LOCS.map((l) => [l.id, 0])));
  const [saving, setSaving] = useState(false);

  const fteByRole = {};
  const detailByRoleLoc = {};
  STAFFING_ROLES.forEach((r) => {
    let total = 0;
    detailByRoleLoc[r.id] = {};
    STAFFING_LOCS.forEach((loc) => {
      const patients = Number(census[loc.id] || 0);
      const ratio = loc.ratios[r.id] || 0;
      const fte = ratio > 0 ? patients / ratio : 0;
      detailByRoleLoc[r.id][loc.id] = fte;
      total += fte;
    });
    fteByRole[r.id] = Math.round(total * 100) / 100;
  });
  const totalFTE = Object.values(fteByRole).reduce((a, b) => a + b, 0);
  const totalCensus = Object.values(census).reduce((a, b) => a + Number(b || 0), 0);

  const save = async () => {
    if (totalCensus === 0) {
      toast.error("Enter at least one census value.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "staffing",
        patient_identifier: org || null,
        responses: { census, fte_by_role: fteByRole, detail: detailByRoleLoc },
        score: totalFTE,
        interpretation: `${Math.round(totalFTE * 10) / 10} FTE recommended for census of ${totalCensus}`,
        notes: notes || null,
      });
      toast.success("Staffing plan saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="staffing-page" wide>
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="staffing-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Staffing Calculator"
        subtitle="Enter current or planned census by level of care. Output shows required FTE per clinical role using conservative anchor ratios." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Organization / site</label>
            <input className="field" value={org} onChange={(e) => setOrg(e.target.value)} placeholder="e.g. Phoenix — North site" data-testid="staffing-org" />
          </div>

          <div className="card p-5">
            <div className="overline mb-3">Patient census by level of care</div>
            <div className="space-y-3">
              {STAFFING_LOCS.map((loc) => (
                <div key={loc.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-navy font-medium">{loc.label}</div>
                  </div>
                  <input
                    type="number"
                    min={0}
                    value={census[loc.id]}
                    onChange={(e) => setCensus({ ...census, [loc.id]: Math.max(0, Number(e.target.value)) })}
                    className="field w-24 text-right"
                    data-testid={`staffing-census-${loc.id}`}
                  />
                </div>
              ))}
            </div>
            <div className="rule my-4" />
            <div className="text-sm text-navy font-medium">Total census: {totalCensus}</div>
          </div>

          <div className="card p-5">
            <div className="overline mb-3">FTE breakdown by role × LOC</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="staffing-breakdown">
                <thead>
                  <tr className="text-left text-ink-muted">
                    <th className="p-2 font-medium">Role</th>
                    {STAFFING_LOCS.map((l) => (
                      <th key={l.id} className="p-2 font-medium text-right">{l.label.split("(")[0].trim().split(" ")[0]}</th>
                    ))}
                    <th className="p-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {STAFFING_ROLES.map((r) => (
                    <tr key={r.id} className="border-t border-line-soft">
                      <td className="p-2 text-navy font-medium">{r.label}</td>
                      {STAFFING_LOCS.map((l) => (
                        <td key={l.id} className="p-2 text-right text-ink-muted">
                          {(detailByRoleLoc[r.id][l.id] || 0).toFixed(2)}
                        </td>
                      ))}
                      <td className="p-2 text-right text-navy font-semibold">{fteByRole[r.id].toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card p-5">
            <label className="label">Notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="staffing-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="staffing-summary">
            <Calculator size={20} strokeWidth={1.5} className="text-teal mb-2" />
            <div className="overline mb-2">Total FTE required</div>
            <div className="text-5xl font-semibold text-navy">{totalFTE.toFixed(1)}</div>
            <p className="text-sm text-ink-muted mt-2 leading-relaxed">
              Based on {totalCensus} patients across {STAFFING_LOCS.length} levels of care.
            </p>

            <div className="rule my-4" />
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-2">Per role</div>
            <ul className="text-sm space-y-1">
              {STAFFING_ROLES.map((r) => (
                <li key={r.id} className="flex items-center justify-between text-ink">
                  <span>{r.label}</span>
                  <span className="text-navy font-medium">{fteByRole[r.id].toFixed(1)}</span>
                </li>
              ))}
            </ul>

            <div className="rule my-4" />
            <button onClick={save} disabled={saving || totalCensus === 0} className="btn btn-primary w-full justify-center" data-testid="staffing-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
            <p className="text-xs text-ink-muted mt-3">
              Ratios are conservative anchors. Adjust based on acuity, payer mix, and state licensing minimums.
            </p>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
